import { useEffect } from "react";

const SITIO = "https://tu-kit.com";

function fijarMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag ?? "meta");
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) {
    if (k !== "tag" && v != null) el.setAttribute(k, v);
  }
  return el;
}

/**
 * Metadatos por ruta.
 *
 * En la página ya prerenderizada estas etiquetas vienen en el HTML que
 * sirve el servidor; esto las mantiene al día durante la navegación
 * del lado del cliente, cuando el documento no se recarga.
 */
export default function Seo({ title, description, path = "/", noindex = false, jsonLd = null }) {
  useEffect(() => {
    const url = `${SITIO}${path === "/" ? "/" : path}`;

    if (title) document.title = title;

    fijarMeta('meta[name="description"]', { tag: "meta", name: "description", content: description });
    fijarMeta('link[rel="canonical"]', { tag: "link", rel: "canonical", href: url });
    fijarMeta('meta[name="robots"]', {
      tag: "meta",
      name: "robots",
      content: noindex ? "noindex, follow" : "index, follow, max-image-preview:large",
    });

    fijarMeta('meta[property="og:title"]', { tag: "meta", property: "og:title", content: title });
    fijarMeta('meta[property="og:description"]', { tag: "meta", property: "og:description", content: description });
    fijarMeta('meta[property="og:url"]', { tag: "meta", property: "og:url", content: url });
    fijarMeta('meta[property="og:type"]', { tag: "meta", property: "og:type", content: "website" });
    fijarMeta('meta[name="twitter:card"]', { tag: "meta", name: "twitter:card", content: "summary_large_image" });

    let script = document.getElementById("jsonld-ruta");
    if (script) script.remove();
    if (jsonLd) {
      script = document.createElement("script");
      script.id = "jsonld-ruta";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, path, noindex, jsonLd]);

  return null;
}

/** Datos estructurados de una calculadora. */
export function jsonLdHerramienta(tool, faqs = []) {
  const url = `${SITIO}/${tool.slug}`;
  const bloques = [
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: tool.name,
      url,
      description: tool.seo.description,
      applicationCategory: "FinanceApplication",
      operatingSystem: "Cualquiera (navegador web)",
      inLanguage: "es-MX",
      offers: { "@type": "Offer", price: "0", priceCurrency: "MXN" },
      publisher: { "@type": "Organization", name: "tu-kit.com", url: SITIO },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Inicio", item: SITIO },
        { "@type": "ListItem", position: 2, name: tool.category, item: `${SITIO}/#${tool.category.toLowerCase()}` },
        { "@type": "ListItem", position: 3, name: tool.name, item: url },
      ],
    },
  ];

  if (faqs.length) {
    bloques.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.pregunta,
        acceptedAnswer: { "@type": "Answer", text: f.respuesta },
      })),
    });
  }

  return bloques;
}

export { SITIO };
